import React, { memo } from 'react';
import { Helmet } from 'react-helmet-async';

export type OGType = 'website' | 'article' | 'profile' | 'music.song' | 'music.album' | 'music.playlist';

export type JsonLdValue = string | number | boolean | null | undefined | JsonLdObject | JsonLdArray;
export interface JsonLdObject { [key: string]: JsonLdValue }
export type JsonLdArray = Array<JsonLdValue>;

export interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: OGType;
  /** @deprecated Google and modern search engines ignore meta keywords. Usage is kept for backward compatibility only. */
  keywords?: string[];
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  structuredData?: JsonLdObject | JsonLdArray;
  fbAppId?: string;
  robots?: string;
  hreflangs?: Array<{ href: string; hreflang: string }>;
  locale?: string;
  alternateLocales?: string[];
  googleSiteVerification?: string;
  bingSiteVerification?: string;
  facebookDomainVerification?: string;
  yandexVerification?: string;
}

const DEFAULT_TITLE = "Independent Electronic Music Record Label & Creative Studio";
const DEFAULT_DESCRIPTION = "AstralVibe is an independent electronic music record label, creative studio, and artist management agency. We specialize in Afro House, Melodic Techno, and cutting-edge electronic sounds, shaping the future of music production and artist development.";
const SITE_NAME = "AstralVibe";
const SITE_URL = "https://astralvibe.art";
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;
const DEFAULT_ROBOTS = "index,follow,max-image-preview:large";
const DEFAULT_LOCALE = "en_US";

// Helper to determine image MIME type
const getImageType = (url: string): string => {
  const lowerUrl = url.toLowerCase();
  if (lowerUrl.endsWith('.png')) return 'image/png';
  if (lowerUrl.endsWith('.webp')) return 'image/webp';
  if (lowerUrl.endsWith('.gif')) return 'image/gif';
  if (lowerUrl.endsWith('.svg')) return 'image/svg+xml';
  return 'image/jpeg'; // Default fallback
};

export const SEO: React.FC<SEOProps> = memo(({ 
  title, 
  description = DEFAULT_DESCRIPTION, 
  image = DEFAULT_IMAGE,
  url = "",
  type = "website",
  // keywords is extracted to prevent unused var warnings, but we no longer render it.
  keywords: _keywords,
  author = SITE_NAME,
  publishedTime,
  modifiedTime,
  structuredData,
  fbAppId,
  robots = DEFAULT_ROBOTS,
  hreflangs,
  locale = DEFAULT_LOCALE,
  alternateLocales,
  googleSiteVerification,
  bingSiteVerification,
  facebookDomainVerification,
  yandexVerification
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | ${DEFAULT_TITLE}`;
  
  // Construct absolute URL
  const path = url.startsWith('/') ? url : `/${url}`;
  const cleanPath = path === '/' ? '' : path;
  const fullUrl = url.startsWith('http') ? url : `${SITE_URL}${cleanPath}`;
  
  // Clean canonical URL without query parameters or hash fragments
  const canonicalUrl = fullUrl.split(/[?#]/)[0];
  
  // Ensure image is absolute URL
  const imageUrl = image.startsWith('http') ? image : `${SITE_URL}${image.startsWith('/') ? image : `/${image}`}`;
  const secureImageUrl = imageUrl.startsWith('https://') ? imageUrl : undefined;
  const imageType = getImageType(imageUrl);

  // Safely serialize JSON-LD to prevent XSS
  const safeJsonLd = structuredData ? JSON.stringify(structuredData).replace(/</g, '\\u003c') : undefined;

  return (
    <Helmet>
      {/* Standard Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {author && <meta name="author" content={author} />}
      <meta name="robots" content={robots} />
      <meta name="theme-color" content="#6025C0" />
      <link rel="canonical" href={canonicalUrl} />

      {/* Hreflang Tags for International SEO */}
      {hreflangs?.map(({ href, hreflang }) => (
        <link key={`${href}-${hreflang}`} rel="alternate" hrefLang={hreflang} href={href} />
      ))}

      {/* Site Verification Tags */}
      {googleSiteVerification && <meta name="google-site-verification" content={googleSiteVerification} />}
      {bingSiteVerification && <meta name="msvalidate.01" content={bingSiteVerification} />}
      {yandexVerification && <meta name="yandex-verification" content={yandexVerification} />}
      {facebookDomainVerification && <meta name="facebook-domain-verification" content={facebookDomainVerification} />}

      {/* Open Graph / Facebook */}
      {fbAppId && <meta property="fb:app_id" content={fbAppId} />}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      {secureImageUrl && <meta property="og:image:secure_url" content={secureImageUrl} />}
      <meta property="og:image:type" content={imageType} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:locale" content={locale} />
      {alternateLocales?.map((altLocale) => (
        <meta key={altLocale} property="og:locale:alternate" content={altLocale} />
      ))}

      {/* Article / Content Specific Tags */}
      {type === 'article' && publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {type === 'article' && modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {type === 'article' && author && <meta property="article:author" content={author} />}

      {/* Twitter - Falling back to Open Graph values for title/description/image to avoid duplication */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@astralvibemusic" />
      <meta name="twitter:creator" content="@astralvibemusic" />

      {/* Structured Data (JSON-LD) - safely rendered */}
      {safeJsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd }} />
      )}
    </Helmet>
  );
});

SEO.displayName = 'SEO';